import React, { useState } from 'react';
import { MapPin, Phone, Clock, Mail, Calendar, Users, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { restaurantInfo, hours } from '../data/mock';

const ContactPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast({
      title: "Reservation Request Sent!",
      description: "We'll contact you within 1 hour to confirm your reservation.",
    });
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      date: '',
      time: '',
      guests: '',
      message: ''
    });
  };

  const currentHour = new Date().getHours();
  const isOpen = currentHour >= 12 && currentHour < 22;

  return (
    <div className="min-h-screen bg-white">
      {/* Contact Hero */}
      <section className="hero-section">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Get in Touch
            </h1>
            <div className="accent-line"></div>
            <p className="text-xl text-gray-600 leading-relaxed">
              Ready to experience authentic African and Antillean cuisine? 
              Contact us for reservations, catering, or any questions about our menu.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {/* Phone */}
            <div className="card-custom text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <Phone size={32} className="text-red-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Call Us</h3>
              <p className="text-gray-600 mb-6">
                Speak directly with our team for immediate assistance
              </p>
              <a 
                href={`tel:${restaurantInfo.phone}`}
                className="btn-primary inline-block"
              >
                {restaurantInfo.phone}
              </a>
              <p className="text-sm text-gray-500 mt-4">
                {isOpen ? 'We\'re currently open' : 'Currently closed - we\'ll call back'}
              </p>
            </div>

            {/* Location */}
            <div className="card-custom text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <MapPin size={32} className="text-green-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Visit Us</h3>
              <p className="text-gray-600 mb-6">
                Come and experience our warm, welcoming atmosphere
              </p>
              <address className="not-italic text-gray-900 font-medium mb-4">
                {restaurantInfo.address}
              </address>
              <button 
                className="btn-secondary"
                onClick={() => window.open('https://maps.google.com?q=Esplanade+des+Récréations+22,+Meyrin', '_blank')}
              >
                Get Directions
              </button>
            </div>

            {/* Online Ordering */}
            <div className="card-custom text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                  <MessageCircle size={32} className="text-orange-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Order Online</h3>
              <p className="text-gray-600 mb-6">
                Quick and convenient delivery to your door
              </p>
              <div className="space-y-3">
                <button 
                  className="btn-primary w-full"
                  onClick={() => window.open('https://www.ubereats.com', '_blank')}
                >
                  Uber Eats
                </button>
                <button 
                  className="btn-accent w-full"
                  onClick={() => window.open('https://www.just-eat.ch', '_blank')}
                >
                  Just Eat
                </button>
              </div>
            </div>
          </div>

          {/* Reservation Form & Hours */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Reservation Form */}
            <div className="card-custom">
              <div className="flex items-center space-x-4 mb-8">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Calendar size={24} className="text-red-600" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900">Make a Reservation</h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-gray-700 font-medium">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      required
                      className="border-gray-300 focus:border-red-600 focus:ring-red-600"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-gray-700 font-medium">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+41 XX XXX XX XX"
                      required
                      className="border-gray-300 focus:border-red-600 focus:ring-red-600"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-medium">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                    className="border-gray-300 focus:border-red-600 focus:ring-red-600"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date" className="text-gray-700 font-medium">Preferred Date *</Label>
                    <Input
                      id="date"
                      name="date"
                      type="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      required
                      className="border-gray-300 focus:border-red-600 focus:ring-red-600"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="time" className="text-gray-700 font-medium">Preferred Time *</Label>
                    <Input
                      id="time"
                      name="time"
                      type="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      min="12:00"
                      max="21:30"
                      required
                      className="border-gray-300 focus:border-red-600 focus:ring-red-600"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="guests" className="text-gray-700 font-medium">Number of Guests *</Label>
                    <Input
                      id="guests"
                      name="guests"
                      type="number"
                      value={formData.guests}
                      onChange={handleInputChange}
                      placeholder="2"
                      min="1"
                      max="20"
                      required
                      className="border-gray-300 focus:border-red-600 focus:ring-red-600"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-gray-700 font-medium">Special Requests</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Any dietary restrictions, special occasions, or other requests..."
                    rows={4}
                    className="border-gray-300 focus:border-red-600 focus:ring-red-600"
                  />
                </div>

                <Button type="submit" className="w-full btn-primary">
                  <Calendar className="mr-2" size={20} />
                  Request Reservation
                </Button>

                <p className="text-sm text-gray-500 text-center">
                  We'll contact you within 1 hour to confirm your reservation.
                  For urgent requests, please call us directly.
                </p>
              </form>
            </div>

            {/* Hours & Info */}
            <div className="space-y-8">
              {/* Opening Hours */}
              <div className="card-custom">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Clock size={24} className="text-green-600" />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">Opening Hours</h2>
                </div>

                <div className="space-y-4">
                  {Object.entries(hours).map(([day, time]) => (
                    <div key={day} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-b-0">
                      <span className={`font-medium ${
                        day === new Date().toLocaleDateString('en-US', { weekday: 'long' }) 
                          ? 'text-gray-900' 
                          : 'text-gray-600'
                      }`}>
                        {day}
                      </span>
                      <span className={`${
                        day === new Date().toLocaleDateString('en-US', { weekday: 'long' }) 
                          ? 'text-gray-900 font-semibold' 
                          : 'text-gray-600'
                      }`}>
                        {time}
                      </span>
                    </div>
                  ))}
                </div>

                <div className={`mt-6 p-4 rounded-lg border ${
                  isOpen 
                    ? 'bg-green-50 border-green-200 text-green-700'
                    : 'bg-red-50 border-red-200 text-red-700'
                }`}>
                  <div className="flex items-center space-x-3">
                    <Clock size={20} />
                    <span className="font-medium">
                      {isOpen ? 'Currently Open' : 'Currently Closed'}
                    </span>
                  </div>
                  <p className="text-sm mt-1">
                    {isOpen ? 'Accepting walk-ins and reservations' : 'Next opening: Tomorrow 12:00 PM'}
                  </p>
                </div>
              </div>

              {/* Additional Info */}
              <div className="card-custom">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <Users size={24} className="text-orange-600" />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">Group Events & Catering</h2>
                </div>

                <div className="space-y-4">
                  <p className="text-gray-600">
                    Planning a special event? We offer catering services for:
                  </p>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <span>Corporate events and meetings</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <span>Birthday parties and celebrations</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <span>Cultural events and festivals</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <span>Private dining for groups (15+ people)</span>
                    </li>
                  </ul>
                  <p className="text-sm text-gray-500 mt-4">
                    Contact us at least 48 hours in advance for group bookings and catering orders.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-padding section-light">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Find Us in Meyrin
            </h2>
            <div className="accent-line"></div>
            <p className="text-gray-600">
              Conveniently located with accessible parking and public transport nearby
            </p>
          </div>
          
          <div className="card-custom">
            <div className="w-full h-80 bg-gray-100 rounded-lg flex items-center justify-center mb-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin size={32} className="text-red-600" />
                </div>
                <p className="text-gray-500 font-medium">Interactive map coming soon</p>
                <p className="text-sm text-gray-400 mt-2">Click "Get Directions" above to view in Google Maps</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Address</h4>
                <p className="text-gray-600">
                  {restaurantInfo.address}
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Parking</h4>
                <p className="text-gray-600">
                  Wheelchair-accessible parking spaces available nearby
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Public Transport</h4>
                <p className="text-gray-600">
                  Bus stops within walking distance, train station 10 minutes away
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;